import { gql, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import * as React from 'react';

import { Loading } from '@/imports/ui/chakra/components/Loading';
import { NoAccess } from '@/imports/ui/chakra/components/NoAccess';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { NotAllowedToViewContent } from '@/imports/ui/pages/group/NotAllowedToViewContent';

import {
  GroupResourcesPane_SingleLibraryFolderVersionFileLink,
  GroupResourcesPane_SingleLibraryFolderVersionFileLinkVariables,
} from './graphql-types/GroupResourcesPane_SingleLibraryFolderVersionFileLink';
import {
  GroupResourcesPane_groupQuery,
  GroupResourcesPane_groupQueryVariables,
} from './graphql-types/GroupResourcesPane_groupQuery';

import { GroupPageLayout } from '../GroupPageLayout';
import { GroupResourcesPaneContent } from './GroupResourcesPaneContent';

const GROUP_RESOURCE_PANE_QUERY = gql`
  query GroupResourcesPane_groupQuery($input: GroupInput!) {
    group(input: $input) {
      result {
        _id
        settings {
          communityVisibility
        }
        currentUserCanEditLibrary
        library {
          _id
          rootFolder {
            _id
          }
        }
        currentUserGroupPermission {
          canViewContent
        }
        isCurrentUserIsAdmin
      }
    }
  }
`;

const SINGLE_LIBRARY_FOLDER_VERSION_FILE_LINK_QUERY = gql`
  query GroupResourcesPane_SingleLibraryFolderVersionFileLink(
    $input: SingleLibraryFolderVersionFileLinkInput!
  ) {
    singleLibraryFolderVersionFileLink(input: $input) {
      _id
      libraryFolderId
      versionFileId
      versionFile {
        ...GroupResourcesPaneContent_singleVersionFileFragment
      }
    }
  }
  ${GroupResourcesPaneContent.singleVersionFileFragment}
`;

export const GroupResourcesPane: React.FC = (props) => {
  const router = useRouter();
  const {
    groupId,
    versionFileId: urlVersionFileId,
    folderId: urlFolderId,
  } = router.query;

  const { loading, error, data } = useQuery<
    GroupResourcesPane_groupQuery,
    GroupResourcesPane_groupQueryVariables
  >(GROUP_RESOURCE_PANE_QUERY, {
    variables: { input: { _id: groupId.toString() } },
  });

  const group = data?.group.result;
  const library = group?.library;
  const communityVisibility = group?.settings.communityVisibility;
  const isCurrentUserIsAdmin = data?.group.result?.isCurrentUserIsAdmin;
  const singleVersionFileToFetch = urlVersionFileId?.toString();
  const {
    data: singleVFLData,
    loading: singleVFLLoading,
  } = useQuery<
    GroupResourcesPane_SingleLibraryFolderVersionFileLink,
    GroupResourcesPane_SingleLibraryFolderVersionFileLinkVariables
  >(SINGLE_LIBRARY_FOLDER_VERSION_FILE_LINK_QUERY, {
    variables: {
      input: {
        groupId: group?._id || '',
        versionFileId: singleVersionFileToFetch,
      },
    },
    // Avoid a useless round-trip (and folder-id churn) when deep-linking without a file.
    skip: !group?._id || !singleVersionFileToFetch,
  });

  const singleVersionFileLink =
    singleVFLData?.singleLibraryFolderVersionFileLink;
  const initialFolderId =
    singleVersionFileLink &&
    singleVersionFileLink.libraryFolderId !== library?.rootFolder._id
      ? singleVersionFileLink.libraryFolderId
      : urlFolderId
      ? urlFolderId.toString()
      : library?.rootFolder._id;

  // Never call router.replace during render — it remounts children and races
  // duplicate libraryFolderContents requests.
  React.useEffect(() => {
    if (
      !initialFolderId ||
      initialFolderId === urlFolderId ||
      initialFolderId === library?.rootFolder._id
    ) {
      return;
    }
    void router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          folderId: initialFolderId,
        },
      },
      undefined,
      { shallow: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when folder ids change
  }, [initialFolderId, library?.rootFolder._id, urlFolderId]);

  // Wait for deep-link folder resolution so Content does not fetch twice
  // (root first, then the file's folder).
  const waitingForDeepLinkFolder =
    !!singleVersionFileToFetch && singleVFLLoading;

  if (loading || waitingForDeepLinkFolder) {
    return <Loading />;
  }

  if (communityVisibility === 'HIDDEN' && !isCurrentUserIsAdmin) {
    return (
      <NoAccess message="This group has been hidden by your administrator and can't be accessed. For questions or concerns, contact your administrator." />
    );
  }
  return (
    <GroupPageLayout>
      {loading ? (
        <Loading />
      ) : error ? (
        <UnexpectedError />
      ) : !group ? (
        <UnexpectedError />
      ) : !group.currentUserGroupPermission?.canViewContent || !library ? (
        <NotAllowedToViewContent />
      ) : (
        <GroupResourcesPaneContent
          currentUserCanEditLibrary={group.currentUserCanEditLibrary}
          library={library}
          groupId={groupId.toString()}
          singleVersionFile={singleVersionFileLink?.versionFile}
          initialFolderId={initialFolderId || library.rootFolder._id}
        />
      )}
    </GroupPageLayout>
  );
};
